import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { MyAccount } from '../../../../../../../models/my-account';
import { AccountService } from '../../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { Membership, MembershipDiscount } from '../../../../../../../models/membership';
import { MembershipService } from '../../../../../../../services/membership/membership.service';
import { Association } from '../../../../../../../models/association';
import { AssociationService } from '../../../../../../../services/association/association.service';
import {
  MembershipCancellationDiscountOfferPreviewModalComponent
} from './modals/membership-cancellation-discount-offer-preview-modal/membership-cancellation-discount-offer-preview-modal.component';
import {
  MembershipDiscountApplyMethod,
  MembershipDiscountType,
  MembershipPurchaseType,
  MembershipStatus
} from '../../../../../../../models/membership-enums';
import { Roster } from '../../../../../../../models/event-type';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';


@Component({
  selector: 'app-membership-discount-detail',
  templateUrl: './membership-discount-detail.component.html',
  styleUrls: ['./membership-discount-detail.component.css']
})
export class MembershipDiscountDetailComponent implements OnInit {
  public isNewDiscount: boolean;
  public discount: MembershipDiscount = {
    id: null,
    url: null,
    discount_apply_method: MembershipDiscountApplyMethod.code,
    code: '',
    name: '',
    discount_type: MembershipDiscountType.currency,
    amount: null,
    is_recurring: false,
    applies_to_add_ons: false,
    applies_to_initiation_fees: false,
    waives_initiation_fees: false,
    start_date: null,
    end_date: null,
    membership_ids: [],
    membership_names: [],
    roster_ids: [],
    roster_names: [],
    requires_association: false,
    requires_all_associations: false,
    association_ids: [],
    association_names: [],
    requires_membership: false,
    required_membership_ids: [],
    required_membership_names: [],
    max_number_of_uses_per_customer: null,
    required_no_check_in_period_days: null,
    applies_to_first_time_members_only: false,
    min_check_in_count: null,
    max_check_in_count: null,
    min_number_of_days_without_membership: null,
    min_age: null,
    max_age: null,
    required_active_duration_days: null,
    can_be_used_with_upfront_payments: true,
    referrer_credit_amount: '0.00',
    max_number_of_referrer_credits_per_month: null,
    referrer_credit_only_applies_to_recurring_payments: false,
    referrer_must_have_active_membership: true,
    may_combine_with_other_discounts: true,
    customer_message: '',
    minimum_duration_before_offer_days: null,
    minimum_duration_before_offer_months: null,
    purchase_alert_message: '',
    purchase_terms_and_conditions: '',
    change_payment_method_alert_message: '',
    change_payment_method_terms_and_conditions: '',
    created_at: null,
    updated_at: null,
    last_updated_by_id: null,
    last_updated_by_first_name: null,
    last_updated_by_last_name: null,
    last_updated_by_email: null,
  };
  public memberships: Membership[];
  public rosters: Roster[];
  public associations: Association[];

  // Used to ensure retired memberships are included as options if they are already on the discount.
  // Otherwise, retired memberships are not shown.
  public initialMembershipIds: number[] = [];

  public errorMessage = '';
  public errors: any = {};

  public loadingExistingDiscount = false;
  public loadingMemberships = true;
  public loadingRosters = true;
  public loadingAssociations = true;
  public loadingSubmit = false;
  public loadingDelete = false;

  public discountNotFound = false;
  public serverError: number = null;

  public myAccount: MyAccount;

  public MembershipDiscountType = MembershipDiscountType;
  public MembershipDiscountApplyMethod = MembershipDiscountApplyMethod;
  public MembershipStatus = MembershipStatus;

  constructor(
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private membershipService: MembershipService,
    private eventTypeService: EventTypeService,
    private associationService: AssociationService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewDiscount = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    // Get the discount to edit, if an ID was provided.
    if (!this.isNewDiscount) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.discountNotFound = true;
        this.loadingExistingDiscount = false;
        return;
      }

      this.loadDiscount(idInt);
    }

    this.loadMemberships();
    this.loadRosters();
    this.loadAssociations();
  }

  public loadDiscount(id: number) {
    this.loadingExistingDiscount = true;

    this.membershipService
      .getMembershipDiscount(id)
      .then(discount => {
        this.discount = discount;
        this.initialMembershipIds = [...this.discount.membership_ids];
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingDiscount = false; });
  }

  public handleSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<MembershipDiscount>;
    if (this.isNewDiscount) {
      savePromise = this.membershipService.addMembershipDiscount(this.discount);
    } else {
      savePromise = this.membershipService.updateMembershipDiscount(this.discount);
    }

    savePromise
      .then(savedDiscount => {
        this.discount = savedDiscount;

        let successMessage: string;
        let discountName = this.discount.name;
        if (!discountName) {
          discountName = this.discount.code;
        }
        if (this.isNewDiscount) {
          successMessage = discountName + ' created successfully.';
        } else {
          successMessage = discountName + ' updated successfully.';
        }

        this.router.navigate(
          ['/membership-settings/discounts'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewDiscount) {
            this.errorMessage = 'You do not have permission to add discounts.';
          } else {
            this.errorMessage = 'You do not have permission to modify this discount.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleDelete() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Delete Discount';
    modalRef.componentInstance.messageText = 'Are you sure you want to delete this discount? This action cannot be undone.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDelete = true;

      this.membershipService
        .deleteMembershipDiscount(this.discount)
        .then(() => {
          let discountName = this.discount.name;
          if (!discountName) {
            discountName = this.discount.code;
          }

          this.router.navigate(
            ['/membership-settings/discounts'],
            {
              state: {
                feedback: {
                  text: discountName + ' deleted.',
                  status: 'success'
                }
              }
            }
          )
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errors = reason.error;
            this.errorMessage = 'Please correct the errors shown and try again.';
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to delete this discount.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDelete = false; });
    }
  }

  public getPotentialDiscountRecipientMemberships(): Membership[] {
    return this.memberships.filter(m => {
      const prepaidBankTransfer = (
        (this.discount.discount_apply_method === MembershipDiscountApplyMethod.bankTransfer) &&
        (m.purchase_type === MembershipPurchaseType.prepaid)
      );
      if (prepaidBankTransfer) { return false; }

      if (m.purchase_type === MembershipPurchaseType.benefit) { return false; }

      if (this.initialMembershipIds.includes(m.id)) { return true; }

      if (m.rolling_event_type_id !== null) { return false; }

      return [MembershipStatus.draft, MembershipStatus.active].includes(m.status);
    })
  }

  public membershipSelected(membership: Membership) {
    return this.discount.membership_ids.includes(membership.id);
  }

  public toggleMembership(membership: Membership) {
    if (this.membershipSelected(membership)) {
      this.discount.membership_ids.splice(this.discount.membership_ids.indexOf(membership.id), 1);
    } else {
      this.discount.membership_ids.push(membership.id);
    }
  }

  public allMembershipsAreSelected() {
    for (const membership of this.getPotentialDiscountRecipientMemberships()) {
      if (!this.discount.membership_ids.includes(membership.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoMembershipsSelected() {
    this.discount.membership_ids = [];
  }

  public setAllMembershipsSelected() {
    this.discount.membership_ids = this.getPotentialDiscountRecipientMemberships().map(m => m.id);
  }

  public rosterSelected(roster: Roster) {
    return this.discount.roster_ids.includes(roster.id);
  }

  public toggleRoster(roster: Roster) {
    if (this.rosterSelected(roster)) {
      this.discount.roster_ids.splice(this.discount.roster_ids.indexOf(roster.id), 1);
    } else {
      this.discount.roster_ids.push(roster.id);
    }
  }

  public allRostersAreSelected() {
    for (const roster of this.rosters) {
      if (!this.discount.roster_ids.includes(roster.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoRostersSelected() {
    this.discount.roster_ids = [];
  }

  public setAllRostersSelected() {
    this.discount.roster_ids = this.rosters.map(r => r.id);
  }

  public associationSelected(association: Association) {
    return this.discount.association_ids.includes(association.id);
  }

  public toggleAssociation(association: Association) {
    if (this.associationSelected(association)) {
      this.discount.association_ids.splice(this.discount.association_ids.indexOf(association.id), 1);
    } else {
      this.discount.association_ids.push(association.id);
    }
  }

  public allAssociationsAreSelected() {
    for (const association of this.associations) {
      if (!this.discount.association_ids.includes(association.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoAssociationsSelected() {
    this.discount.association_ids = [];
  }

  public setAllAssociationsSelected() {
    this.discount.association_ids = this.associations.map(ep => ep.id);
  }

  public requiredMembershipSelected(membership: Membership) {
    return this.discount.required_membership_ids.includes(membership.id);
  }

  public toggleRequiredMembership(membership: Membership) {
    if (this.requiredMembershipSelected(membership)) {
      this.discount.required_membership_ids.splice(
        this.discount.required_membership_ids.indexOf(membership.id),
        1
      );
    } else {
      this.discount.required_membership_ids.push(membership.id);
    }
  }

  public allRequiredMembershipsAreSelected() {
    for (const membership of this.memberships) {
      if (!this.discount.required_membership_ids.includes(membership.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoRequiredMembershipsSelected() {
    this.discount.required_membership_ids = [];
  }

  public setAllRequiredMembershipsSelected() {
    this.discount.required_membership_ids = this.memberships.map(m => m.id);
  }

  public handleDiscountTypeChange() {
    if (this.discount.discount_apply_method === MembershipDiscountApplyMethod.referral) {
      this.discount.discount_type = MembershipDiscountType.currency;
    }
    if (this.discount.discount_apply_method === MembershipDiscountApplyMethod.cancellation) {
      this.discount.requires_membership = false;
      this.discount.applies_to_first_time_members_only = false;
    }
    if ([MembershipDiscountApplyMethod.referral,
         MembershipDiscountApplyMethod.cancellation].includes(this.discount.discount_apply_method)) {
      this.discount.is_recurring = false;
    }
    if (this.discount.discount_apply_method === MembershipDiscountApplyMethod.freeze) {
      this.discount.waives_initiation_fees = false;
      this.discount.min_number_of_days_without_membership = null;
      this.discount.applies_to_first_time_members_only = false;
      this.discount.can_be_used_with_upfront_payments = true;
    }
    if (this.discount.discount_apply_method === MembershipDiscountApplyMethod.bankTransfer) {
      // The Duration control is hidden for bank transfer discounts, so clear
      // the first-time-member restriction (whose recurring explanation would
      // otherwise show for an apply method that never enforces it).
      this.discount.applies_to_first_time_members_only = false;
    }
  }

  public handleDiscountDurationChange() {
    if (!this.discount.is_recurring) {
      // Required Active Duration only applies to recurring discounts.
      this.discount.required_active_duration_days = null;
    }
  }

  public isLoading() {
    return (
      this.loadingExistingDiscount ||
      this.loadingMemberships ||
      this.loadingAssociations ||
      this.loadingSubmit ||
      this.loadingDelete
    )
  }

  public openCancellationDiscountOfferPreviewModal() {
    const modalRef = this.modalService.open(MembershipCancellationDiscountOfferPreviewModalComponent);
    modalRef.componentInstance.customerMessage = this.discount.customer_message;
  }

  public showAppliesToFirstTimeMembersOnlyField() {
    return (
      (this.discount.discount_apply_method !== MembershipDiscountApplyMethod.cancellation) &&
      (this.discount.discount_apply_method !== MembershipDiscountApplyMethod.bankTransfer) &&
      (this.discount.discount_apply_method !== MembershipDiscountApplyMethod.freeze)
    ) || (
      this.discount.applies_to_first_time_members_only
    );
  }

  private loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        3000,
        {include_rolling: true}
      )
      .then(result => {
        this.memberships = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingMemberships = false; });
  }

  private loadRosters() {
    this.loadingRosters = true;

    this.eventTypeService
      .getRosters(1, 3000, {})
      .then(result => {
        this.rosters = result.results;

        this.rosters.sort((a, b) => {
          if (a.event_type_name < b.event_type_name) { return -1; }
          if (a.event_type_name > b.event_type_name) { return 1; }
          if (a.name < b.name) { return -1; }
          if (a.name > b.name) { return 1; }
          return 0;
        })
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRosters = false; });
  }

  private loadAssociations() {
    this.loadingAssociations = true;

    this.associationService
      .getAssociations(1, 3000, {})
      .then(result => {
        this.associations = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociations = false; });
  }
}
